import { useMemo, useState } from "react";
import { FiCheck, FiChevronDown, FiChevronUp } from "react-icons/fi";
import type { TFunction } from "i18next";
import { SegmentedControl } from "@/components/ui/SegmentedControl";
import {
  FILTER_LABEL_CLASSNAME,
  FILTER_OPTION_CLASSNAME,
  FILTER_VALUE_IDLE_CLASSNAME,
  FILTER_VALUE_SELECTED_CLASSNAME,
} from "@/components/ui/filterPanelStyles";
import { SearchInput } from "@/components/ui/SearchInput";

type DirectionFilter = "all" | "income" | "expense" | "transfer";

export type TransactionFilterGroup = {
  id: string;
  label: string;
  emptyLabel: string;
  options: { id: number; label: string }[];
  values: number[];
  clear: () => void;
  toggle: (value: number) => void;
};

export type TransactionActiveFilter = {
  id: string;
  label: string;
  remove: () => void;
};

const groupSummary = (t: TFunction<["common", "transactions"]>, group: TransactionFilterGroup) => {
  if (group.values.length === 0) return group.emptyLabel;
  if (group.values.length === 1) {
    return group.options.find((option) => option.id === group.values[0])?.label ?? group.emptyLabel;
  }
  return t("transactions.groupSelectedCount", { count: group.values.length, defaultValue: "{{count}} selected" });
};

interface FilterGroupPanelProps {
  t: TFunction<["common", "transactions"]>;
  group: TransactionFilterGroup;
  onClose: () => void;
}

const FilterGroupPanel = ({ t, group, onClose }: FilterGroupPanelProps) => {
  const [query, setQuery] = useState("");
  // Snapshot taken on open so selected options lead the list without re-ordering under the pointer.
  const [selectedOnOpen] = useState(() => new Set(group.values));
  const normalizedQuery = query.trim().toLocaleLowerCase();

  const orderedOptions = useMemo(
    () =>
      [...group.options].sort(
        (a, b) => Number(selectedOnOpen.has(b.id)) - Number(selectedOnOpen.has(a.id)),
      ),
    [group.options, selectedOnOpen],
  );
  const visibleOptions = normalizedQuery
    ? orderedOptions.filter((option) => option.label.toLocaleLowerCase().includes(normalizedQuery))
    : orderedOptions;

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex min-h-12 flex-none items-center gap-2 border-b border-[var(--app-border)] px-3">
        <button
          type="button"
          onClick={onClose}
          aria-expanded
          className="app-focus-ring flex min-h-11 min-w-0 flex-1 items-center gap-2 rounded-xl px-1 text-left"
        >
          <span className={`truncate ${FILTER_LABEL_CLASSNAME}`}>{group.label}</span>
          <FiChevronUp className="ml-auto h-4 w-4 flex-none text-[var(--app-muted-text)]" />
        </button>
        {group.values.length > 0 ? (
          <button
            type="button"
            onClick={group.clear}
            className={`app-focus-ring inline-flex min-h-11 flex-none items-center px-2 ${FILTER_OPTION_CLASSNAME} font-semibold text-[var(--app-text-secondary)] transition hover:text-[var(--app-text)]`}
          >
            {t("common.actions.clear", { defaultValue: "Clear" })}
          </button>
        ) : null}
      </div>

      <div className="flex-none px-3 pt-1">
        <SearchInput
          value={query}
          onChange={setQuery}
          placeholder={t("common.actions.search", { defaultValue: "Search" })}
          label={`${t("common.actions.search", { defaultValue: "Search" })} — ${group.label}`}
          size="compact"
        />
      </div>

      <div className="min-h-0 flex-1 divide-y divide-[var(--app-border)] overflow-y-auto overscroll-contain px-3 py-1">
        {visibleOptions.map((option) => {
          const selected = group.values.includes(option.id);
          return (
            <button
              type="button"
              key={option.id}
              onClick={() => group.toggle(option.id)}
              aria-pressed={selected}
              className={`app-focus-ring flex min-h-11 w-full items-center gap-2 rounded-xl px-3 text-left ${FILTER_OPTION_CLASSNAME} leading-tight transition ${
                selected
                  ? `bg-[var(--app-segmented-active-bg)] ${FILTER_VALUE_SELECTED_CLASSNAME}`
                  : "text-[var(--app-text-secondary)] hover:bg-[var(--app-row-hover-bg)] hover:text-[var(--app-text-secondary)]"
              }`}
            >
              <span className="min-w-0 flex-1 truncate">{option.label}</span>
              {selected ? <FiCheck className="h-4 w-4 flex-none text-[var(--app-primary)]" /> : null}
            </button>
          );
        })}
        {visibleOptions.length === 0 ? (
          <p className={`px-3 py-2 ${FILTER_OPTION_CLASSNAME} text-[var(--app-muted-text)]`}>
            {t("transactions.noFilterMatches", { defaultValue: "No matches" })}
          </p>
        ) : null}
      </div>
    </div>
  );
};

interface TransactionFilterOptionsProps {
  t: TFunction<["common", "transactions"]>;
  selectedDirection: DirectionFilter;
  setSelectedDirection: (value: DirectionFilter) => void;
  directionOptions: { id: DirectionFilter; label: string }[];
  filterGroups: TransactionFilterGroup[];
}

/** Opciones de filtro (tipo de movimiento + grupos) para el popover «Filtros». */
export const TransactionFilterOptions = ({
  t,
  selectedDirection,
  setSelectedDirection,
  directionOptions,
  filterGroups,
}: TransactionFilterOptionsProps) => {
  const [openGroupId, setOpenGroupId] = useState<string | null>(null);
  const availableGroups = filterGroups.filter((group) => group.options.length > 0);
  const activeGroup = availableGroups.find((group) => group.id === openGroupId) ?? null;

  return (
    <div className="flex flex-col gap-3">
      <p className="app-domain-sidebar-label">
        {t("transactions.typeFilterLabel", { defaultValue: "Movement type" })}
      </p>
      <SegmentedControl
        value={selectedDirection}
        onChange={setSelectedDirection}
        options={directionOptions}
      />

      {availableGroups.length > 0 ? (
        <div
          className="app-panel-inset flex flex-col overflow-visible"
          style={{ height: `${Math.max(12, availableGroups.length * 3)}rem` }}
        >
          {activeGroup ? (
            <FilterGroupPanel
              key={activeGroup.id}
              t={t}
              group={activeGroup}
              onClose={() => setOpenGroupId(null)}
            />
          ) : (
            availableGroups.map((group) => (
              <button
                type="button"
                key={group.id}
                onClick={() => setOpenGroupId(group.id)}
                aria-expanded={false}
                className="app-focus-ring flex min-h-11 flex-1 items-center gap-3 border-b border-[var(--app-border)] px-4 text-left transition last:border-b-0 hover:bg-[var(--app-row-hover-bg)]"
              >
                <span className={`flex-none ${FILTER_LABEL_CLASSNAME}`}>{group.label}</span>
                <span
                  className={`ml-auto min-w-0 truncate ${FILTER_OPTION_CLASSNAME} ${group.values.length > 0 ? FILTER_VALUE_SELECTED_CLASSNAME : FILTER_VALUE_IDLE_CLASSNAME}`}
                >
                  {groupSummary(t, group)}
                </span>
                <FiChevronDown className="h-4 w-4 flex-none text-[var(--app-muted-text)]" />
              </button>
            ))
          )}
        </div>
      ) : null}
    </div>
  );
};
